import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import centroid from '@turf/centroid';
import destination from '@turf/destination';
import intersect from '@turf/intersect';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import * as helpers from '@turf/helpers';

import { FEEDBACK_WIZARD_STEP } from '../../../common/utils/helpers/routes';
import { round } from '../../../common/utils/helpers/math';
import { getItemType, isPOIItem } from '../../../common/utils/helpers/item';
import { selectActiveItem } from '../../../pages/item-details/store';
import {
  markerDragged,
  markerInitialized,
  markerPositionCountryCodeChanged,
  markerRemoved,
  routablePointLocationChanged,
  selectLocation,
  selectRoutablePoints,
  setRoutablePointActive,
} from '../../../common/store/item-edit';
import { ITEM_TYPES } from '../../../common/utils/constants/constants';
import { useMobile } from '../../../common/components/responsive/Responsive';
import { useGlMap } from '../../contexts/context';
import {
  checkZoomEditable,
  createCirclePolygon,
  getLocationCountryCode,
} from '../../utils/helpers/utils';
import { itemMobileCenteringAnimationOptions } from '../../utils/constants/constants';
import { useRetryingAnimationRunner } from '../../utils/hooks/useRetryingAnimationRunner';
import { useMapWaterPolygon } from '../../contexts/mapWaterPolygonContext';
import {
  OMIT_POLYGON_TYPES,
  createOmitPolygons,
} from '../../utils/helpers/omitPolygonsUtils';
import { useMapPadding } from '../../utils/hooks/useMapPadding';
import { useItemFocus } from '../../utils/hooks/useItemFocus';
import { performInterruptionSafeAnimation } from '../../utils/helpers/animationUtils';
import { useMapBoundsPolygon } from '../../utils/hooks/useMapBoundsPolygon';
import { selectZoom } from '../../store';
import { IconName } from '../../../common/components/icon/Icon.tsx';

const EDIT_LOCATION_CIRCLE_RADIUS = 8.04672; /* kilometers equal to 5 miles */
const ROUTABLE_POINT_COLLISION_RADIUS = 0.01;
const NEW_ROUTABLE_POINT_ITEM_CENTER_OMIT_DISTANCE = 0.015;

interface RouteMatch {
  wizardStep;
}

export const useMapItemEdit = () => {
  const dispatch = useDispatch();
  const animationRunner = useRetryingAnimationRunner();
  useMapPadding({ animationRunner });
  const isMobile = useMobile();
  const match = useRouteMatch<RouteMatch>();
  const glMap = useGlMap();

  const item = useSelector(selectActiveItem);
  useItemFocus(item, animationRunner);

  // there may be non-point items, like roads
  const pointItem = useMemo(() => {
    const itemType = getItemType(item);
    return itemType === ITEM_TYPES.address || itemType === ITEM_TYPES.poi
      ? item
      : null;
  }, [item]);

  const routablePoints = useSelector(selectRoutablePoints);

  const markerLocation = useSelector(selectLocation);
  const markerLocationRef = useRef();
  markerLocationRef.current = markerLocation;

  const maxMoveDistancePolygon = useMemo(
    () =>
      pointItem
        ? createCirclePolygon(
            pointItem.geometry.coordinates,
            EDIT_LOCATION_CIRCLE_RADIUS,
          )
        : null,
    [pointItem],
  );
  const boundsPolygon = useMapBoundsPolygon(glMap);
  const mapWaterPolygon = useMapWaterPolygon();

  const correctMarkerCoordinates = useMemo(() => {
    if (!mapWaterPolygon || !boundsPolygon || !maxMoveDistancePolygon) {
      return () => null;
    }

    return createOmitPolygons([
      {
        geometry: intersect(maxMoveDistancePolygon, boundsPolygon),
        type: OMIT_POLYGON_TYPES.allow,
      },
      {
        type: OMIT_POLYGON_TYPES.restrict,
        geometry: mapWaterPolygon,
      },
    ]);
  }, [mapWaterPolygon, boundsPolygon, maxMoveDistancePolygon]);

  const inactiveRoutablePointsOmitPolygons = useMemo(
    () =>
      routablePoints
        ? routablePoints
            .filter((point) => !point.active && point.location)
            .map((point) => ({
              geometry: createCirclePolygon(
                point.location,
                ROUTABLE_POINT_COLLISION_RADIUS,
              ),
              type: OMIT_POLYGON_TYPES.restrict,
            }))
        : [],
    [routablePoints],
  );

  const correctActiveRoutablePointCoordinates = useMemo(() => {
    if (!mapWaterPolygon || !boundsPolygon) {
      return () => null;
    }

    return createOmitPolygons([
      {
        geometry: boundsPolygon,
        type: OMIT_POLYGON_TYPES.allow,
      },
      ...inactiveRoutablePointsOmitPolygons,
      { geometry: mapWaterPolygon, type: OMIT_POLYGON_TYPES.restrict },
    ]);
  }, [boundsPolygon, inactiveRoutablePointsOmitPolygons, mapWaterPolygon]);

  const isBusinessEdit = useMemo(() => isPOIItem(item), [item]);
  const wizardStep = useMemo(() => match.params.wizardStep, [match]);
  const editingRoutablePoints = useMemo(
    () => wizardStep === FEEDBACK_WIZARD_STEP.routablePoints,
    [wizardStep],
  );
  // flag used on mobile to show that location is selectable depending on the wizard step
  const locationSelectable = useMemo(() => {
    return (
      !editingRoutablePoints &&
      (!isMobile ||
        (isBusinessEdit
          ? wizardStep === FEEDBACK_WIZARD_STEP.location
          : wizardStep === FEEDBACK_WIZARD_STEP.feedback))
    );
  }, [isBusinessEdit, isMobile, wizardStep, editingRoutablePoints]);

  const handleMarkerDragEnd = useCallback(
    (location) => {
      const cutLocation = location.map(round.fp());

      if (!isEqual(cutLocation, markerLocationRef.current)) {
        dispatch(markerDragged(cutLocation));

        const locationCountryCode = getLocationCountryCode(glMap, location);
        if (locationCountryCode) {
          dispatch(markerPositionCountryCodeChanged(locationCountryCode));
        }
      }
    },
    [dispatch, glMap],
  );

  const handlePointDragStart = useCallback(
    (idx) => dispatch(setRoutablePointActive(idx)),
    [dispatch],
  );

  const handlePointDragEnd = useCallback(
    (idx, location) => {
      const cutLocation = location.map(round.fp());

      if (!isEqual(cutLocation, routablePoints[idx].location)) {
        dispatch(routablePointLocationChanged(idx, cutLocation));
      }

      if (!isMobile) {
        dispatch(setRoutablePointActive());
      }
    },
    [dispatch, routablePoints, isMobile],
  );

  const itemCenter = useMemo(
    () =>
      pointItem
        ? centroid(pointItem.geometry).geometry.coordinates.map(round.fp())
        : null,
    [pointItem],
  );

  // marker initialization
  useEffect(() => {
    if (!itemCenter) {
      dispatch(markerRemoved());
      return;
    }

    dispatch(
      markerInitialized({
        location: itemCenter,
      }),
    );
  }, [item, dispatch, itemCenter]);

  const itemCurrentPosition = useMemo(
    () => markerLocation || itemCenter,
    [itemCenter, markerLocation],
  );

  // routable point location initialization
  useEffect(() => {
    if (
      !editingRoutablePoints ||
      !itemCurrentPosition ||
      !boundsPolygon ||
      !booleanPointInPolygon(helpers.point(markerLocation), boundsPolygon)
    ) {
      return;
    }

    // find just added point or point with removed location, it's ok to process points one by one, because the effect
    // reacts on routable points change and so it will be triggered once again just to chech if there is a point not processed
    const newRoutablePointIndex = routablePoints.findIndex(
      (point) => !point.location,
    );
    if (newRoutablePointIndex === -1) {
      return;
    }

    const itemCenterOmitPolygon = createCirclePolygon(
      itemCurrentPosition,
      NEW_ROUTABLE_POINT_ITEM_CENTER_OMIT_DISTANCE,
    );

    // there still may be the case when map viewport is far away and it's not possible to apply bounds "allow" polygon
    // this case should be handled on water polygon changes or on submit
    const correctNewRoutablePointCoordinates = createOmitPolygons([
      {
        geometry: boundsPolygon,
        type: OMIT_POLYGON_TYPES.allow,
      },
      ...inactiveRoutablePointsOmitPolygons,
      { geometry: itemCenterOmitPolygon, type: OMIT_POLYGON_TYPES.restrict },
      { geometry: mapWaterPolygon, type: OMIT_POLYGON_TYPES.restrict },
    ]);

    const routablePointRandomLocation = destination(
      itemCurrentPosition,
      NEW_ROUTABLE_POINT_ITEM_CENTER_OMIT_DISTANCE,
      // random angle in range from [-180;180]
      Math.random() * 360 - 180,
    ).geometry.coordinates.map(round.fp());

    dispatch(
      routablePointLocationChanged(
        newRoutablePointIndex,
        correctNewRoutablePointCoordinates(routablePointRandomLocation),
      ),
    );
  }, [
    dispatch,
    routablePoints,
    inactiveRoutablePointsOmitPolygons,
    mapWaterPolygon,
    itemCurrentPosition,
    boundsPolygon,
    markerLocation,
    editingRoutablePoints,
  ]);

  const zoom = useSelector(selectZoom);
  const zoomRef = useRef<number>(0);
  zoomRef.current = zoom;

  // update marker location if it's on water
  useEffect(() => {
    if (
      !locationSelectable ||
      !checkZoomEditable(zoomRef.current) ||
      !markerLocation ||
      !boundsPolygon ||
      !booleanPointInPolygon(helpers.point(markerLocation), boundsPolygon)
    ) {
      return;
    }

    const correctedMarkerCoordinates = correctMarkerCoordinates(markerLocation);
    handleMarkerDragEnd(correctedMarkerCoordinates || itemCenter);
  }, [
    handleMarkerDragEnd,
    markerLocation,
    boundsPolygon,
    correctMarkerCoordinates,
    itemCenter,
    locationSelectable,
  ]);

  // update routable points locations if they're on water
  useEffect(() => {
    if (
      !editingRoutablePoints ||
      !checkZoomEditable(zoomRef.current) ||
      !boundsPolygon ||
      !routablePoints
    ) {
      return;
    }

    // find just added point with no location
    const initializedRoutablePoints = routablePoints
      // recreate points to enchance it with index and for further modification below
      .map((point, index) => ({
        ...point,
        index,
      }))
      .filter((point) => !!point.location);

    if (!initializedRoutablePoints.length) {
      return;
    }

    // go through every routable point
    initializedRoutablePoints.forEach(({ index, ...point }) => {
      if (!booleanPointInPolygon(point.location, boundsPolygon)) {
        return;
      }

      const otherInitializedRoutablePointsOmitPolygons =
        initializedRoutablePoints
          .filter((point) => point.index !== index)
          .map((point) => ({
            geometry: createCirclePolygon(
              point.location,
              ROUTABLE_POINT_COLLISION_RADIUS,
            ),
            type: OMIT_POLYGON_TYPES.restrict,
          }));

      const correctRoutablePointCoordinates = createOmitPolygons([
        {
          geometry: boundsPolygon,
          type: OMIT_POLYGON_TYPES.allow,
        },
        ...otherInitializedRoutablePointsOmitPolygons,
        { geometry: mapWaterPolygon, type: OMIT_POLYGON_TYPES.restrict },
      ]);

      const correctedRoutablePointCoordinates = correctRoutablePointCoordinates(
        point.location,
      )?.map(round.fp());

      if (!isEqual(correctedRoutablePointCoordinates, point.location)) {
        // update coordinates for further points omit polygons construction
        initializedRoutablePoints.find(
          (point) => point.index === index,
        )!.location = correctedRoutablePointCoordinates;

        dispatch(
          routablePointLocationChanged(
            index,
            correctedRoutablePointCoordinates,
          ),
        );
      }
    });
  }, [
    boundsPolygon,
    dispatch,
    editingRoutablePoints,
    mapWaterPolygon,
    routablePoints,
  ]);

  const activeRoutablePoint = useMemo(
    () => routablePoints && routablePoints.find((point) => point.active),
    [routablePoints],
  );
  // animate map center to be on marker location or on active routable point on mobile
  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (isMobile) {
      let animationCenter;

      if (locationSelectable) {
        if (
          markerLocation &&
          !isEqual(glMap.getCenter().toArray(), markerLocation)
        ) {
          animationCenter = markerLocation;
        }
      } else {
        if (
          activeRoutablePoint &&
          !isEqual(glMap.getCenter().toArray(), activeRoutablePoint.location)
        ) {
          animationCenter = activeRoutablePoint.location;
        }
      }

      if (animationCenter) {
        return animationRunner(
          (animationDuration) =>
            performInterruptionSafeAnimation(
              glMap,
              (duration) =>
                glMap.easeTo({
                  center: animationCenter,
                  ...itemMobileCenteringAnimationOptions,
                  duration,
                }),
              animationDuration,
            ),
          { animationDuration: itemMobileCenteringAnimationOptions.duration },
        );
      }
    }
  }, [
    activeRoutablePoint,
    animationRunner,
    glMap,
    isMobile,
    locationSelectable,
    markerLocation,
  ]);

  const overlayParams: { icon: IconName; text: string } | undefined =
    useMemo(() => {
      const zoomAllowEditLocation = checkZoomEditable(zoom);

      if (pointItem && !zoomAllowEditLocation) {
        return {
          icon: 'magnifying-glass-add',
          text: 'Zoom in to edit object location',
        };
      }
    }, [pointItem, zoom]);

  return {
    item,
    pointItem,
    locationSelectable,
    markerLocation,
    maxMoveDistancePolygon,
    routablePoints,
    editingRoutablePoints,
    isMobile,
    correctMarkerCoordinates,
    correctActiveRoutablePointCoordinates,
    handleMarkerDragEnd,
    handlePointDragStart,
    handlePointDragEnd,
    overlayParams,
  };
};
